import { analytics } from '@lib/analytics/page-events';
import { ProductListV2 } from '@lib/types/api/ProductListV2';
import type { ListData } from 'ui-components';

import { addItemToList, removeItemFromList } from './productListsApi';

export type ProductInfo = { id: string; name: string };

export const toListData = (
  list: ProductListV2,
  personal: boolean = true
): ListData => ({
  id: list.id,
  name: list.name,
  count: list.productCount,
  type: list.visibility,
  personal,
});

export function getListChanges(
  initialListIds: string[],
  selectedLists: ListData[]
) {
  const initialSet = new Set(initialListIds);
  const selectedIdSet = new Set(selectedLists.map((l) => l.id));

  const listsToAdd = selectedLists.filter((l) => !initialSet.has(l.id));
  const idsToRemove = initialListIds.filter((id) => !selectedIdSet.has(id));

  return { listsToAdd, idsToRemove };
}

export async function syncListChanges({
  listsToAdd,
  idsToRemove,
  productId,
  getOrgIdForList,
}: {
  listsToAdd: ListData[];
  idsToRemove: string[];
  productId: string;
  getOrgIdForList: (listId: string) => string | undefined;
}): Promise<void> {
  await Promise.all([
    ...listsToAdd.map((l) =>
      addItemToList(l.id, productId, getOrgIdForList(l.id))
    ),
    ...idsToRemove.map((id) =>
      removeItemFromList(id, productId, getOrgIdForList(id))
    ),
  ]);
}

export type SaveToastContent = { title: string; listLabel: string };

export function buildSaveToastContent({
  addedNames,
  removedNames,
  microcopy,
}: {
  addedNames: string[];
  removedNames: string[];
  microcopy: { savedTo: string; removedFrom: string; multipleLists: string };
}): SaveToastContent | null {
  const format = (names: string[]) =>
    names.length === 1
      ? names[0]
      : microcopy.multipleLists.replace('{count}', String(names.length));

  if (addedNames.length > 0) {
    return { title: microcopy.savedTo, listLabel: format(addedNames) };
  }
  if (removedNames.length > 0) {
    return { title: microcopy.removedFrom, listLabel: format(removedNames) };
  }
  return null;
}

export function trackListChangesAnalytics({
  listsToAdd,
  idsToRemove,
  productId,
  productName,
}: {
  listsToAdd: ListData[];
  idsToRemove: string[];
  productId: string;
  productName: string;
}): void {
  listsToAdd.forEach(() =>
    analytics.pushSaveToListEvent({
      asset_id: productId,
      asset_name: productName,
    })
  );
  idsToRemove.forEach(() =>
    analytics.pushRemoveFromListEvent({
      asset_id: productId,
      asset_name: productName,
    })
  );
}
